<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.customerPass.actions.claim.title')"
    :info="$t('components.customerPass.actions.claim.info')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('components.customerPass.actions.claim.confirm'), type: 'confirm', variant: 'primary', callback: confirm },
    ]"
    :loading="loading"
    :portal="portal"
  >
    <div class="pt-4 text-left -mx-4 bg-gray-50 border-t border-b px-4 dark:bg-gray-900">
      <o-form v-if="customerPass" ref="form">
        <div v-if="customerPass.gift" class="mb-4 p-3 bg-white dark:bg-gray-800 rounded-md border dark:border-gray-700 text-sm space-y-1">
          <p v-if="customerPass.gift.to.name">
            <span class="text-gray-500 dark:text-gray-400 mr-1">{{ $t('general.recipientName') }}:</span>
            <span>{{ customerPass.gift.to.name }}</span>
          </p>
          <p v-if="customerPass.gift.to.email">
            <span class="text-gray-500 dark:text-gray-400 mr-1">{{ $t('general.recipientEmail') }}:</span>
            <span>{{ customerPass.gift.to.email }}</span>
          </p>
          <p v-if="customerPass.gift.from.name">
            <span class="text-gray-500 dark:text-gray-400 mr-1">{{ $t('general.senderName') }}:</span>
            <span>{{ customerPass.gift.from.name }}</span>
          </p>
        </div>

        <o-field-group
          v-slot="{ status }"
          :label="$t('general.customer')"
          :name="$t('general.customer')"
          class="block mb-4"
          rules="required"
        >
          <customer-select v-model="customer" preview create default-status-class="bg-white h-10 dark:bg-gray-900" :status="status" />
        </o-field-group>
      </o-form>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import CustomerSelect from '@/components/customer/elements/Select.vue'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (event: 'update:loading', value: boolean): void
  (event: 'success', value: any): void
}

withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $bus, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const form = ref<any>()
const customerPass = ref<any>(null)
const customer = ref<any>(null)

const open = (pass: any) => {
  customerPass.value = pass
  customer.value = null
  dialog.value.open()
}

const confirm = async () => {
  if (!await form.value?.validate()) return false

  emit('update:loading', true)

  try {
    const next = customerPass.value.copy()
    next.customer = customer.value
    next.claimable = false

    const response = await $api.customerPass.save(next)
    const claimedPass = response.data

    if (!claimedPass) {
      $notification.error({ errors: response.errors })
      return false
    }

    emit('success', claimedPass)

    $bus.emit.updated('CustomerPass', claimedPass)

    $notification.toast({
      title: $i18n.t('components.customerPass.actions.claim.success'),
      text: $i18n.t('components.customerPass.actions.claim.successInfo'),
      variant: 'success',
    })

    return true
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    return false
  } finally {
    emit('update:loading', false)
  }
}

defineExpose({ open })
</script>
